// ===============================
// RefundExecutionPanel.tsx
// ===============================

import React, { useMemo, useState } from "react";
import { RefundsService, RequestsService } from "../../../../api";

type PaymentInfo = {
  status: string;
  totalPaid: number;
  totalPayable: number;
  payments?: any[];
};

type RefundProps = {
  requestId: number;
  adminId: number;
  paymentInfo: PaymentInfo;
  onRefundComplete?: () => void;
};

const RefundExecutionPanel: React.FC<RefundProps> = ({
  requestId,
  adminId,
  paymentInfo,
  onRefundComplete,
}) => {
  const [deduction, setDeduction] = useState(0);
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(false);

  const refundableAmount = useMemo(() => {
    return Math.max(paymentInfo.totalPaid - deduction, 0);
  }, [paymentInfo.totalPaid, deduction]);

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const refund =
        await RefundsService.createRequestRefundsRequestPost(
          requestId,
          adminId,
          "Cancellation",
          remarks || "Refund initiated due to cancellation"
        );

      await RefundsService.calculateRefundsRefundIdCalculatePost(refund.id);

      await RefundsService.overrideRefundsRefundIdOverridePost(
        refund.id,
        refundableAmount,
        remarks || "Admin override applied"
      );

      await RefundsService.initiateRefundsRefundIdInitiatePost(refund.id);

      await RequestsService.updateServiceRequestStatusServiceRequestsRequestIdStatusPut(
        requestId,
        {
          main_status: "CANCELLATION",
          sub_status: "Refund Processed",
          notes: `Refund processed successfully. Refund ID: ${refund.id}`,
        }
      );

      alert("Refund processed successfully");
      onRefundComplete?.();
    } catch (err) {
      console.error(err);
      alert("Refund initiation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setDeduction(0);
    setRemarks("");
  };

  return (
    <div className="border rounded-lg p-4 bg-white shadow-sm space-y-3">
      {/* Financial Rows */}
      <h3 className="text-lg font-semibold">Refund Processing</h3>
      <div className="grid grid-cols-[160px_140px] gap-y-3 items-center text-sm">
        {/* Total Paid */}
        <span className="font-medium">Total Paid</span>
        <input
          type="text"
          readOnly
          value={`₹${paymentInfo.totalPaid}`}
          className="border rounded px-2 py-1 text-right bg-gray-50"
        />

        {/* Deductions */}
        <span className="font-medium">Deductions</span>
        <input
          type="number"
          min={0}
          max={paymentInfo.totalPaid}
          value={deduction}
          onChange={(e) => setDeduction(Number(e.target.value))}
          className="border rounded px-2 py-1 text-right"
        />

        {/* Refundable */}
        <span className="font-medium">Refundable</span>
        <input
          type="text"
          readOnly
          value={`₹${refundableAmount}`}
          className="border rounded px-2 py-1 text-right bg-gray-50"
        />
      </div>

      {/* Remarks */}
      <div className="grid grid-cols-[160px_1fr] gap-2 items-center text-sm">
        <span className="font-medium">Remarks</span>
        <input
          type="text"
          maxLength={150}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Details of deduction, etc"
          className="border rounded px-3 py-1 w-full"
        />
      </div>

      {/* Buttons */}
      <div className="flex justify-start gap-2 pt-2">
        <button
          onClick={handleCancel}
          disabled={loading}
          className="px-4 py-1 border rounded bg-gray-100 hover:bg-gray-200"
        >
          Cancel
        </button>

        <button
          onClick={handleSubmit}
          disabled={loading}
          className="px-4 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
        >
          Submit
        </button>
      </div>
    </div>
  );
};

export default RefundExecutionPanel;